/**
 * Sistema de Animações Centralizado
 * Gerencia animações de surgimento baseadas em Intersection Observer
 */

(function() {
  'use strict';

  // Verificar se o usuário prefere movimento reduzido
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Inicializa animação para um elemento específico
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções de configuração
   * @param {Function} [options.onVisible] - Callback quando elemento fica visível
   * @param {string} [options.animatedClass] - Classe que indica que o elemento está animado
   */
  function initAnimation(selector, options = {}) {
    const element = document.querySelector(selector);
    const animatedClass = options.animatedClass || '--animated';
    
    if (!element || !element.className.includes(animatedClass)) {
      return;
    }

    // Se usuário prefere movimento reduzido, mostrar imediatamente
    if (prefersReducedMotion) {
      element.classList.add('is-visible');
      if (options.onVisible) {
        options.onVisible(element);
      }
      return;
    }

    // Função para verificar se está visível e animar
    function checkAndAnimate() {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        // Se já está visível, animar imediatamente
        element.classList.add('is-visible');
        if (options.onVisible) {
          options.onVisible(element);
        }
        return true;
      }
      return false;
    }

    // Verificar imediatamente
    if (checkAndAnimate()) {
      return;
    }

    // Intersection Observer para detectar quando o elemento está visível
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (options.onVisible) {
            options.onVisible(entry.target);
          }
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(element);
  }

  /**
   * Inicializa animação de divider
   * @param {string} selector - Seletor CSS do divider line
   */
  function initDividerAnimation(selector) {
    initAnimation(selector, {
      animatedClass: 'divider__line--animated'
    });
  }

  /**
   * Inicializa animação de typewriter
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções do typewriter
   * @param {number} options.speed - Velocidade em ms entre cada letra
   * @param {number} options.delay - Delay antes de iniciar
   */
  function initTypewriter(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const originalText = element.textContent || element.innerText;
    const originalHTML = element.innerHTML;

    // Limpar o conteúdo
    element.innerHTML = '';
    element.style.opacity = '1';

    // Adicionar letras uma por uma
    let currentIndex = 0;
    
    function addNextChar() {
      if (currentIndex >= originalText.length) {
        // Restaurar HTML original
        element.innerHTML = originalHTML;
        element.classList.add('typewriter-complete');
        return;
      }

      const char = originalText[currentIndex];
      const textNode = document.createTextNode(char);
      element.appendChild(textNode);

      currentIndex++;
      setTimeout(addNextChar, speed);
    }

    // Iniciar após o delay
    setTimeout(addNextChar, delay);
  }

  /**
   * Inicializa múltiplas animações de typewriter em uma lista
   * @param {string} selector - Seletor CSS base (deve retornar múltiplos elementos)
   * @param {Object} options - Opções do typewriter
   */
  function initTypewriterList(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const staggerDelay = options.staggerDelay || 200; // Delay entre cada elemento

    elements.forEach((element, index) => {
      const originalText = element.getAttribute('data-original-text') || element.textContent || element.innerText;
      const originalHTML = element.getAttribute('data-original-html') || element.innerHTML;

      // Limpar o conteúdo
      element.innerHTML = '';
      element.style.opacity = '1';

      // Adicionar letras uma por uma
      let currentIndex = 0;
      
      function addNextChar() {
        if (currentIndex >= originalText.length) {
          // Restaurar HTML original
          element.innerHTML = originalHTML;
          element.classList.add('typewriter-complete');
          return;
        }

        const char = originalText[currentIndex];
        const textNode = document.createTextNode(char);
        element.appendChild(textNode);

        currentIndex++;
        setTimeout(addNextChar, speed);
      }

      // Iniciar após o delay + stagger
      setTimeout(addNextChar, delay + (index * staggerDelay));
    });
  }

  /**
   * Inicializa animações para todos os elementos animados em um container
   * Usado quando conteúdo é inserido dinamicamente (ex: filtros, morph)
   * @param {HTMLElement} container - Container onde buscar elementos
   */
  function initInContainer(container) {
    if (!container) return;

    const selector = '[class*="--animated"]:not(.is-visible)';
    const elements = container.querySelectorAll(selector);
    if (elements.length === 0) return;

    elements.forEach(function(element) {
      if (prefersReducedMotion) {
        element.classList.add('is-visible');
        return;
      }

      function checkAndAnimate() {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          element.classList.add('is-visible');
          return true;
        }
        return false;
      }

      if (checkAndAnimate()) return;

      const observerOptions = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      observer.observe(element);
    });
  }

  /**
   * Retorna filhos elegíveis para animação em cascata
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getCascadeChildren(container) {
    return Array.from(container.children).filter(function(child) {
      if (child.classList.contains('view-product-title')) return false;
      if (child.classList.contains('jv-showcase-floating-images')) return false;
      if (child.classList.contains('jv-showcase-floating-images-block')) return false;
      if (child.querySelector && child.querySelector(':scope > .jv-showcase-floating-images')) return false;
      if (child.className && typeof child.className === 'string' && child.className.indexOf('--animated') !== -1) {
        return false;
      }
      return true;
    });
  }

  function runCascadeAnimation(container) {
    const initialDelay = parseInt(container.dataset.cascadeInitialDelay, 10) || 0;
    const stagger = parseInt(container.dataset.cascadeStagger, 10) || 250;

    getCascadeChildren(container).forEach(function(item, index) {
      const delay = initialDelay + (index * stagger);
      item.style.animationDelay = delay + 'ms';

      setTimeout(function() {
        item.classList.add('cascade-visible');
      }, 10);
    });
  }

  const cascadeWatchers = new WeakMap();
  const CASCADE_MAX_RETRIES = 30;
  const CASCADE_RETRY_INTERVAL = 50;

  function resetCascade(container) {
    if (!container) return;

    delete container.dataset.cascadeInitialized;

    getCascadeChildren(container).forEach(function(item) {
      item.classList.remove('cascade-visible');
      item.style.animationDelay = '';
      item.style.opacity = '';
    });

    const watchers = cascadeWatchers.get(container);
    if (watchers) {
      watchers.intersectionObserver.disconnect();
      if (watchers.childObserver) watchers.childObserver.disconnect();
      if (watchers.visibilityObserver) watchers.visibilityObserver.disconnect();
      cascadeWatchers.delete(container);
    }
  }

  /**
   * Inicializa animação em cascata nos filhos diretos de um container
   * @param {HTMLElement} container - Elemento com data-cascade-enabled="true"
   * @param {Object} [options] - Opções adicionais
   * @param {string} [options.visibilityParentSelector] - Aguarda visibilidade do ancestral (ex.: slideshow-slide)
   * @param {boolean} [options.reset] - Reinicia observadores e estado antes de observar
   */
  function initCascade(container, options = {}) {
    if (!container || container.dataset.cascadeEnabled !== 'true') return;

    if (options.reset) {
      resetCascade(container);
    }

    if (container.dataset.cascadeInitialized === 'true') return;

    if (cascadeWatchers.has(container)) {
      resetCascade(container);
    }

    const visibilityParentSelector = options.visibilityParentSelector
      || container.dataset.cascadeVisibilityParent
      || '';
    const visibilityParent = visibilityParentSelector
      ? container.closest(visibilityParentSelector)
      : null;

    function triggerCascade() {
      if (container.dataset.cascadeInitialized === 'true') return;

      const items = getCascadeChildren(container);
      if (items.length === 0) return;

      container.dataset.cascadeInitialized = 'true';

      const watchers = cascadeWatchers.get(container);
      if (watchers) {
        watchers.intersectionObserver.disconnect();
        if (watchers.childObserver) watchers.childObserver.disconnect();
        cascadeWatchers.delete(container);
      }

      if (prefersReducedMotion) {
        items.forEach(function(item) {
          item.style.opacity = '1';
          item.classList.add('cascade-visible');
        });
        return;
      }

      runCascadeAnimation(container);
    }

    function isActiveContext() {
      if (visibilityParent && visibilityParent.getAttribute('aria-hidden') === 'true') {
        return false;
      }
      return true;
    }

    function isInViewport() {
      const rect = container.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    function attemptTrigger(requireViewport) {
      if (!isActiveContext()) return;
      if (requireViewport && !isInViewport()) return;
      triggerCascade();
    }

    const intersectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        attemptTrigger(false);
      });
    }, {
      root: null,
      rootMargin: '100px 0px',
      threshold: 0
    });

    intersectionObserver.observe(container);

    const childObserver = new MutationObserver(function() {
      attemptTrigger(true);
    });

    childObserver.observe(container, {
      childList: true,
      subtree: false
    });

    let visibilityObserver = null;
    if (visibilityParent) {
      visibilityObserver = new MutationObserver(function() {
        attemptTrigger(true);
      });
      visibilityObserver.observe(visibilityParent, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }

    cascadeWatchers.set(container, {
      intersectionObserver: intersectionObserver,
      childObserver: childObserver,
      visibilityObserver: visibilityObserver
    });

    attemptTrigger(true);
  }

  /**
   * Inicializa todas as cascatas no escopo (documento ou secção)
   * @param {ParentNode} [scope]
   * @param {Object} [options]
   * @param {boolean} [options.reset]
   */
  function initAllCascades(scope, options = {}) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const containers = root.querySelectorAll('[data-cascade-enabled="true"]');

    containers.forEach(function(container) {
      initCascade(container, {
        reset: options.reset === true,
        visibilityParentSelector: container.dataset.cascadeVisibilityParent || ''
      });
    });
  }

  function retryUninitializedCascades(scope, attempt) {
    attempt = typeof attempt === 'number' ? attempt : 0;
    const root = scope && scope.querySelectorAll ? scope : document;
    const containers = root.querySelectorAll('[data-cascade-enabled="true"]');
    let pending = false;

    containers.forEach(function(container) {
      if (container.dataset.cascadeInitialized === 'true') return;

      pending = true;
      resetCascade(container);
      initCascade(container, {
        visibilityParentSelector: container.dataset.cascadeVisibilityParent || ''
      });
    });

    if (pending && attempt < CASCADE_MAX_RETRIES) {
      window.setTimeout(function() {
        retryUninitializedCascades(scope, attempt + 1);
      }, CASCADE_RETRY_INTERVAL);
    }
  }

  function bootstrapCascades() {
    initAllCascades(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapCascades);
  } else {
    bootstrapCascades();
  }

  window.addEventListener('load', function() {
    retryUninitializedCascades(document, 0);
  });

  window.addEventListener('pagereveal', function() {
    retryUninitializedCascades(document, 0);
  });

  document.addEventListener('shopify:section:load', function(event) {
    if (event.target instanceof HTMLElement) {
      initAllCascades(event.target, { reset: true });
      retryUninitializedCascades(event.target, 0);
    }
  });

  // Exportar funções para uso global
  window.AnimationSystem = {
    init: initAnimation,
    initInContainer: initInContainer,
    initDivider: initDividerAnimation,
    initTypewriter: initTypewriter,
    initTypewriterList: initTypewriterList,
    initCascade: initCascade,
    initAllCascades: initAllCascades,
    resetCascade: resetCascade
  };

})();

